import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

import Loader from 'src/components/loader/Loader';
import API from 'src/utils/api';
import rightArrowIcon from 'src/icons/right-arrow.svg';
import Button from 'src/components/button/Button';
import TransactionsTable from 'src/components/transactions-table/TransactionsTable';
import { Link, useNavigate } from 'react-router-dom';
import Portfolio from 'src/components/portfolio/Portfolio';
import DepositModal from 'src/components/deposit-modal/DepositModal';
import ErrorState from 'src/components/error-state/ErrorState';

const Home = () => {
    const navigate = useNavigate();
    // API Data
    const [totalBalance, setTotalBalance] = useState();
    const [balances, setBalances] = useState();
    const [prices, setPrices] = useState();
    const [transactions, setTransactions] = useState();
    const [accounts, setAccounts] = useState();
    const [withdrawPendingtransactions, setWithdrawPendingTransactions] = useState();
    // API states
    const [loading, setLoading] = useState(true);
    const fetchingBalances = useRef(false);
    const fetchingTransactions = useRef(false);
    const fetchingPendingWithdrawTransactions = useRef(false);
    const [error, setError] = useState(false);
    // Deposit modal
    const [depositModalOpen, setDepositModalOpen] = useState(false);

    useEffect(() => {
        if (fetchingBalances.current) return; // make sure to fetch data only at first render
        const fetchBalances = async () => {
            fetchingBalances.current = true;
            try {
                const response = await API.get('/accounts/balances');

                const filteredBalances = response.data.balances.filter((balance) => balance.amount > 0);
                const pricesResponse = response.data.prices;
                const totalUSDBalance = filteredBalances.reduce(
                    (total, balance) => total + pricesResponse[balance.token] * balance.amount,
                    0
                );

                setBalances(filteredBalances);
                setPrices(pricesResponse);
                setTotalBalance(totalUSDBalance);

                fetchingBalances.current = false;
            } catch (error) {
                setError(true);
                toast.error('Failed to fetch');
                fetchingBalances.current = false;
            }
        };

        fetchBalances();
    }, []);

    useEffect(() => {
        if (fetchingTransactions.current) return; // make sure to fetch data only at first render
        const fetchTransactions = async () => {
            fetchingTransactions.current = true;
            try {
                const response = await API.get('/accounts/transactions');
                const txs = response.data.transactions;
                setAccounts(response.data.accounts);
                setTransactions(txs.slice(0, 8));
                fetchingTransactions.current = false;
            } catch (error) {
                setError(true);
                toast.error('Failed to fetch');
                fetchingTransactions.current = false;
            }
        };

        fetchTransactions();
    }, []);

    // get pending withdraw transactions when the modal is closed and at first renders
    useEffect(() => {
        if (fetchingPendingWithdrawTransactions.current) return;
        const fetchPendingWithdrawTransactions = async () => {
            setLoading(true);
            fetchingPendingWithdrawTransactions.current = true;
            try {
                const response = await API.get('/accounts/pending-withdraw-tx');
                const txs = response.data.transactions;
                setWithdrawPendingTransactions(txs);
                fetchingPendingWithdrawTransactions.current = false;
            } catch (error) {
                setError(true);
                toast.error('Failed to fetch');
                fetchingPendingWithdrawTransactions.current = false;
            }
        };

        fetchPendingWithdrawTransactions();
    }, []);

    useEffect(() => {
        if ((transactions && totalBalance && balances) || error) setLoading(false);
    }, [balances, error, totalBalance, transactions]);

    const onWithdrawClick = () => {
        navigate('/withdraw', { replace: true });
    };

    const onDepositClick = () => setDepositModalOpen(true);

    if (loading) {
        return (
            <div className="w-screen h-screen flex justify-center items-center">
                <Loader />
            </div>
        );
    }

    if (error) {
        return <ErrorState />;
    }

    return (
        <div className="w-screen flex flex-col lg:flex-row px-4 md:px-10 pt-24 max-w-screen-2xl mx-auto">
            <div className="w-full lg:w-1/3 mr-4">
                <div className="w-full bg-gray-light drop-shadow-md rounded-xl flex flex-col py-6 px-8">
                    <span className="mb-5">Total balance</span>
                    <span className="text-5xl font-bold mb-4">${totalBalance.toFixed(2)}</span>
                </div>
                <div className="flex mt-6 w-full">
                    <Button primary text="Deposit" className="w-1/2 mr-2" onClick={onDepositClick} />
                    <Button text="Withdraw" className="w-1/2 ml-2" onClick={onWithdrawClick} />
                </div>
                <Portfolio balances={balances} prices={prices} />
            </div>
            <div className="w-full lg:w-2/3 lg:ml-4 mb-8">
                <div className="w-full xl:bg-gray-light xl:p-6 rounded-xl">
                    <div className="flex justify-between">
                        <span className="mb-4 lg-mb-0">Transactions</span>
                        <Link to="/all-transactions" className="text-gray-darker font-bold text-xs flex items-center">
                            View all <img src={rightArrowIcon} alt="right arrow" className="ml-2" />
                        </Link>
                    </div>
                    <TransactionsTable
                        transactions={[...withdrawPendingtransactions, ...transactions]}
                        accounts={accounts}
                    />
                </div>
            </div>
            {depositModalOpen && (
                <DepositModal
                    isOpen={depositModalOpen}
                    setIsOpen={setDepositModalOpen}
                    addresses={Object.keys(accounts).map((network) => ({ address: accounts[network], network }))}
                />
            )}
        </div>
    );
};

export default Home;
